// internal-deps
import { isEmpty } from "@swiggy-private/js-utils";
import { format } from "@swiggy-private/core-ui";

// interfaces
import { IDineCashSplit, SplitMethod, Contact, GuestSplitsMap } from "@dineout-payment-success-ui/interfaces";

// constants
import {
    DEFAULT_SHARE_VALUE,
    SELF_IDENTIFIER,
    PERCENTAGE_VALIDATION_MESSAGE,
    AMOUNT_DIFFERENCE_LESS,
    AMOUNT_DIFFERENCE_MORE,
    PERCENTAGE_BASIS_POINTS_MAX,
    ZERO_SPLIT_AMOUNT_MESSAGE,
} from "@dineout-payment-success-ui/constants";
import { INR_CURRENCY_SYMBOL_TEXT } from "@dineout-payment-success-ui/constants/Constants";
import {
    roundMoney2,
    rupeesToPaise,
    paiseToRupees,
    percentageToBasisPoints,
    basisPointsToPercentage,
} from "@dineout-payment-success-ui/utils/splitBillMoney";

interface ValidationResult {
    isValid: boolean;
    message?: string;
}

export const getActiveDineCashSplit = (dineCashSplit?: IDineCashSplit): IDineCashSplit | undefined => {
    if (isEmpty(dineCashSplit) || (dineCashSplit.dineCashAmount ?? 0) <= 0) {
        return undefined;
    }

    return dineCashSplit;
};

/**
 * INR amount for a single row from its % of the bill only (rounded paise).
 * Used for % UI subtext so editing another guest does not change this row's amount.
 */
export function rupeesFromPercentageRowOnly(billAmount: number, percentage: number): number {
    const totalPaise = rupeesToPaise(billAmount);
    const bp = percentageToBasisPoints(percentage);
    return paiseToRupees(Math.round((totalPaise * bp) / PERCENTAGE_BASIS_POINTS_MAX));
}

/**
 * Initialize guest splits based on contacts and bill amount (two decimal precision).
 * Remainder paise / basis points go to self so totals match the bill / 100%.
 */
export const initializeGuestSplits = (contacts: Contact[], billAmount: number): GuestSplitsMap => {
    if (!contacts.length) {
        return {} as GuestSplitsMap;
    }

    const totalPaise = rupeesToPaise(billAmount);
    const n = contacts.length;

    const basePercentageBp = Math.floor(PERCENTAGE_BASIS_POINTS_MAX / n);
    const percentageRemainderBp = PERCENTAGE_BASIS_POINTS_MAX - basePercentageBp * n;

    const basePaise = Math.floor(totalPaise / n);
    const amountRemainderPaise = totalPaise - basePaise * n;

    return contacts.reduce((acc, contact) => {
        const isSelf = contact.number === SELF_IDENTIFIER;
        acc[contact.number] = {
            percentage: basisPointsToPercentage(basePercentageBp + (isSelf ? percentageRemainderBp : 0)),
            amount: paiseToRupees(basePaise + (isSelf ? amountRemainderPaise : 0)),
            share: DEFAULT_SHARE_VALUE,
        };
        return acc;
    }, {} as GuestSplitsMap);
};

/**
 * Calculate equal splits for all guests based on selected method (two decimal precision; remainder to self).
 */
export const calculateEqualSplits = (
    guestSplits: GuestSplitsMap,
    contacts: Contact[],
    selectedTab: SplitMethod,
    billAmount: number,
): GuestSplitsMap => {
    const newSplits = { ...guestSplits };

    const totalPaise = rupeesToPaise(billAmount);
    const n = contacts.length;

    const basePercentageBp = Math.floor(PERCENTAGE_BASIS_POINTS_MAX / n);
    const percentageRemainderBp = PERCENTAGE_BASIS_POINTS_MAX - basePercentageBp * n;

    const basePaise = Math.floor(totalPaise / n);
    const amountRemainderPaise = totalPaise - basePaise * n;

    contacts.forEach((contact) => {
        const isSelf = contact.number === SELF_IDENTIFIER;
        if (selectedTab === SplitMethod.PERCENTAGE) {
            newSplits[contact.number] = {
                ...newSplits[contact.number],
                percentage: basisPointsToPercentage(basePercentageBp + (isSelf ? percentageRemainderBp : 0)),
            };
        } else if (selectedTab === SplitMethod.AMOUNT) {
            newSplits[contact.number] = {
                ...newSplits[contact.number],
                amount: paiseToRupees(basePaise + (isSelf ? amountRemainderPaise : 0)),
            };
        } else if (selectedTab === SplitMethod.SHARE) {
            newSplits[contact.number] = { ...newSplits[contact.number], share: DEFAULT_SHARE_VALUE };
        }
    });

    return newSplits;
};

/**
 * Convert splits to actual INR amounts (two decimals).
 * PERCENTAGE: each non–self line uses that row's % only (same as UI); self line in the payload
 * gets the paise remainder so the total matches the bill. SHARE: floor non–self, self gets remainder.
 */
export const convertSplitsToActualAmounts = (
    splits: Record<string, number>,
    method: SplitMethod,
    contacts: Contact[],
    billAmount: number,
): Record<string, number> => {
    const actualSplits: Record<string, number> = {};
    const totalPaise = rupeesToPaise(billAmount);

    if (method === SplitMethod.AMOUNT) {
        contacts.forEach((contact) => {
            actualSplits[contact.number] = roundMoney2(splits[contact.number] || 0);
        });
        return actualSplits;
    }

    if (method === SplitMethod.PERCENTAGE) {
        let allocatedNonSelfPaise = 0;
        contacts.forEach((contact) => {
            if (contact.number === SELF_IDENTIFIER) {
                return;
            }
            const pct = splits[contact.number] || 0;
            const bp = percentageToBasisPoints(pct);
            const rowPaise = Math.round((totalPaise * bp) / PERCENTAGE_BASIS_POINTS_MAX);
            actualSplits[contact.number] = paiseToRupees(rowPaise);
            allocatedNonSelfPaise += rowPaise;
        });
        if (contacts.some((c) => c.number === SELF_IDENTIFIER)) {
            actualSplits[SELF_IDENTIFIER] = paiseToRupees(totalPaise - allocatedNonSelfPaise);
        }
        return actualSplits;
    }

    const totalShares = Object.values(splits).reduce((sum, share) => sum + share, 0);
    const others = contacts.filter((c) => c.number !== SELF_IDENTIFIER);
    let allocatedPaise = 0;

    others.forEach((contact) => {
        const splitValue = splits[contact.number] || 0;
        const sharePaise = Math.floor((totalPaise * splitValue) / Math.max(1, totalShares));
        actualSplits[contact.number] = paiseToRupees(sharePaise);
        allocatedPaise += sharePaise;
    });

    const selfPaise = totalPaise - allocatedPaise;
    if (contacts.some((c) => c.number === SELF_IDENTIFIER)) {
        actualSplits[SELF_IDENTIFIER] = paiseToRupees(selfPaise);
    }

    return actualSplits;
};

/**
 * Calculate actual amount per person for analytics (keys by contact name; amounts match payload rounding).
 */
export const calculateEachPersonShare = (
    splits: Record<string, number>,
    method: SplitMethod,
    contacts: Contact[],
    billAmount: number,
): Record<string, number> => {
    const byPhone = convertSplitsToActualAmounts(splits, method, contacts, billAmount);
    const eachPersonShare: Record<string, number> = {};
    contacts.forEach((contact) => {
        eachPersonShare[contact.name] = byPhone[contact.number] ?? 0;
    });
    return eachPersonShare;
};

/**
 * Extract splits based on selected tab/method
 */
export const extractSplitsByMethod = (
    guestSplits: GuestSplitsMap,
    contacts: Contact[],
    selectedTab: SplitMethod,
): Record<string, number> => {
    const splits: Record<string, number> = {};

    contacts.forEach((contact) => {
        const split = guestSplits[contact.number];
        if (isEmpty(split)) {
            return;
        }
        if (selectedTab === SplitMethod.PERCENTAGE) {
            splits[contact.number] = split.percentage;
        } else if (selectedTab === SplitMethod.AMOUNT) {
            splits[contact.number] = split.amount;
        } else if (selectedTab === SplitMethod.SHARE) {
            splits[contact.number] = split.share;
        }
    });

    return splits;
};

/**
 * Get display amount based on split method (two decimal INR).
 * PERCENTAGE: each row shows only that row's share of the bill (editing someone else does not change this subtext).
 */
export const getDisplayAmount = (
    phoneNumber: string,
    guestSplits: GuestSplitsMap,
    selectedTab: SplitMethod,
    billAmount: number,
    contacts: Contact[],
    // eslint-disable-next-line max-params
): number => {
    const split = guestSplits[phoneNumber];
    if (isEmpty(split)) {
        return 0;
    }

    if (selectedTab === SplitMethod.PERCENTAGE) {
        return rupeesFromPercentageRowOnly(billAmount, split.percentage ?? 0);
    }

    const splits = extractSplitsByMethod(guestSplits, contacts, selectedTab);
    const actualAmounts = convertSplitsToActualAmounts(splits, selectedTab, contacts, billAmount);
    return roundMoney2(actualAmounts[phoneNumber] || 0);
};

/**
 * Validate splits based on split method (integer paise / basis points equality).
 */
export const validateSplits = (
    guestSplits: GuestSplitsMap,
    contacts: Contact[],
    selectedTab: SplitMethod,
    billAmount: number,
): ValidationResult => {
    if (selectedTab === SplitMethod.SHARE) {
        return { isValid: true };
    }

    if (selectedTab === SplitMethod.PERCENTAGE) {
        // Check for zero percentage
        for (const contact of contacts) {
            const percentage = guestSplits[contact.number]?.percentage ?? 0;
            if (percentage === 0) {
                return {
                    isValid: false,
                    message: ZERO_SPLIT_AMOUNT_MESSAGE,
                };
            }
        }

        const totalBp = contacts.reduce(
            (sum, contact) => sum + percentageToBasisPoints(guestSplits[contact.number]?.percentage ?? 0),
            0,
        );
        if (totalBp !== PERCENTAGE_BASIS_POINTS_MAX) {
            return {
                isValid: false,
                message: PERCENTAGE_VALIDATION_MESSAGE,
            };
        }
        return { isValid: true };
    }

    if (selectedTab === SplitMethod.AMOUNT) {
        // Check for zero amount
        for (const contact of contacts) {
            const amount = guestSplits[contact.number]?.amount ?? 0;
            if (amount === 0) {
                return {
                    isValid: false,
                    message: ZERO_SPLIT_AMOUNT_MESSAGE,
                };
            }
        }

        const totalPaise = contacts.reduce(
            (sum, contact) => sum + rupeesToPaise(guestSplits[contact.number]?.amount ?? 0),
            0,
        );
        const billPaise = rupeesToPaise(billAmount);
        if (totalPaise !== billPaise) {
            const diffPaise = billPaise - totalPaise;
            const formattedAmount = format(INR_CURRENCY_SYMBOL_TEXT, paiseToRupees(Math.abs(diffPaise)).toFixed(2));
            const message =
                diffPaise > 0
                    ? format(AMOUNT_DIFFERENCE_LESS, formattedAmount)
                    : format(AMOUNT_DIFFERENCE_MORE, formattedAmount);
            return {
                isValid: false,
                message,
            };
        }
    }

    return { isValid: true };
};
